import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { logErrorSecurely } from "@/lib/errorSanitizer";
import AdminLogin from "@/components/AdminLogin";

interface AdminProtectionProps {
  children: React.ReactNode;
}

const AdminProtection = ({ children }: AdminProtectionProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [hasAdminRole, setHasAdminRole] = useState(false);
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (authLoading) return;
    checkAdminAccess();
  }, [user, authLoading]);

  const checkAdminAccess = async () => {
    setIsLoading(true);

    if (!user) {
      setHasAdminRole(false);
      setIsLoading(false);
      return;
    }

    const timeoutId = setTimeout(() => setIsLoading(false), 8000);

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();

      clearTimeout(timeoutId);

      if (error) {
        logErrorSecurely('AdminProtection', error);
        toast({
          title: "Access Check Failed",
          description: "Unable to verify admin permissions.",
          variant: "destructive",
        });
        setHasAdminRole(false);
        setIsLoading(false);
        return;
      }

      setHasAdminRole(!!data);
    } catch (err) {
      clearTimeout(timeoutId);
      logErrorSecurely('AdminProtection', err);
      setHasAdminRole(false);
    }

    setIsLoading(false);
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-8 w-8 animate-spin mx-auto mb-4 text-trading-primary" />
          <p>Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <AdminLogin />;
  }

  if (!hasAdminRole) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You don't have permission to access the admin panel
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/')} 
              className="w-full mt-4"
              variant="outline"
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default AdminProtection;